import { useState, useRef, useEffect, useCallback, Fragment } from 'react';
import { flushSync } from 'react-dom';
import { motion } from 'framer-motion';
import { SpeakerAvatar, AVATAR_COLORS } from "@/components/SpeakerAvatar";

// --- Helper functions ---

function msToDuration(ms: number, showHours: boolean): string {
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (showHours) {
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

/** Strip leading "00:" from HH:MM:SS timestamps when video is under 1 hour */
function formatTimestamp(ts: string, showHours: boolean): string {
  if (showHours) return ts;
  if (ts.length === 8 && ts.startsWith('00:')) return ts.slice(3);
  return ts;
}

function parseTimeToMs(input: string): number | null {
  const parts = input.trim().split(':').map(Number);
  if (parts.some(isNaN)) return null;
  if (parts.length === 2) return (parts[0] * 60 + parts[1]) * 1000;
  if (parts.length === 3) return (parts[0] * 3600 + parts[1] * 60 + parts[2]) * 1000;
  return null;
}

// Minimum child segment duration — mirrors voiceprint_worker.MIN_SEGMENT_MS.
// If voiceprint_worker.MIN_SEGMENT_MS is raised, this must be raised with it.
// See routes_auth_library.split_segment.MIN_CHILD_MS for the backend enforcement.
const MIN_CHILD_MS = 1000;

// --- Interfaces ---

interface WordInfo {
  text: string;
  start_ms: number;
  end_ms: number;
  confidence?: number;
}

interface Speaker {
  speaker: string;
  text: string;
  timestamp?: string;
  start_ms?: number;
  end_ms?: number;
  words?: WordInfo[];
  segment_id?: string;
  user_override?: true;
}

interface SpeakerMeta {
  id: string;
  display_name: string;
  role?: string;
  bio?: string;
  confidence?: number;
  image_url?: string;
}

interface TranscriptDisplayProps {
  transcript: Speaker[];
  editable?: boolean;
  onEditText?: (index: number, newText: string) => void;
  onEditSpeaker?: (oldName: string, newName: string) => void;
  /** Rename only the given segment (per-segment speaker edit). */
  onEditSpeakerSingle?: (segmentIdx: number, newName: string) => void;
  highlightIndex?: number;
  durationMs?: number;
  speakerMetas?: SpeakerMeta[];
  currentTimeMs?: number;
  hasAudioBarAbove?: boolean;
  onSeekAudio?: (ms: number) => void;
  initialScrollToIndex?: number;
  onInitialScrollComplete?: () => void;
  /** Called when user requests a segment split via gesture. Third arg always undefined post-2026-05-12. */
  onSplit?: (segmentIdx: number, splitAtMs: number, newSpeaker?: undefined) => Promise<void>;
  onEditSegmentTs?: (index: number, startMs: number, endMs: number) => void;
  onEditWordTs?: (segIdx: number, wordIdx: number, startMs: number, endMs: number) => void;
  /** Per-segment speaker override. Feature-flag-gated: VITE_FEATURE_SEGMENT_SPEAKER_OVERRIDE. */
  onSegmentSpeakerOverride?: (segmentId: string, newSpeaker: string) => Promise<void>;
  /** Merge this segment with the one before it. */
  onMergeWithPrev?: (segmentIdx: number) => Promise<void>;
  /** Merge this segment with the one after it. */
  onMergeWithNext?: (segmentIdx: number) => Promise<void>;
  /** Called by the per-segment Save button — persists all pending edits to backend. */
  onSaveAndExit?: () => void | Promise<void>;
}

export const SEGMENT_ID_PREFIX = 'segment-';

export function TranscriptDisplay({
  transcript,
  editable = false,
  onEditText,
  onEditSpeaker,
  onEditSpeakerSingle,
  highlightIndex,
  durationMs,
  speakerMetas = [],
  currentTimeMs = 0,
  hasAudioBarAbove = false,
  onSeekAudio,
  initialScrollToIndex,
  onInitialScrollComplete,
  onSplit,
  onEditSegmentTs,
  onEditWordTs,
  onMergeWithPrev,
  onMergeWithNext,
  onSaveAndExit,
}: TranscriptDisplayProps) {
  const lastScrolledIndex = useRef<number>(-1);
  const [activeSegmentIndex, setActiveSegmentIndex] = useState<number>(-1);
  const lastScrollTime = useRef<number>(0);
  const SCROLL_THROTTLE_MS = 150;
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const hasInitialScrolled = useRef(false);
  const [initialHighlightIndex, setInitialHighlightIndex] = useState<number | null>(null);

  useEffect(() => {
    if (currentTimeMs <= 0) {
      setActiveSegmentIndex(-1);
      return;
    }
    let targetIndex = -1;
    for (let i = 0; i < transcript.length; i++) {
      const seg = transcript[i];
      const startMs = seg.start_ms ?? 0;
      const endMs = seg.end_ms ?? startMs + 10000;
      if (currentTimeMs >= startMs && currentTimeMs < endMs) {
        targetIndex = i;
        break;
      }
      if (currentTimeMs >= startMs) {
        targetIndex = i;
      }
    }
    if (targetIndex !== activeSegmentIndex) {
      setActiveSegmentIndex(targetIndex);
    }
    const now = Date.now();
    if (targetIndex >= 0 && targetIndex !== lastScrolledIndex.current) {
      if (now - lastScrollTime.current > SCROLL_THROTTLE_MS) {
        lastScrolledIndex.current = targetIndex;
        lastScrollTime.current = now;
        const el = document.getElementById(`${SEGMENT_ID_PREFIX}${targetIndex}`);
        if (el) {
          // Page-level scroll: only re-center when the active segment has drifted
          // out of the middle band of the viewport, to avoid jitter on every tick.
          const elRect = el.getBoundingClientRect();
          const viewportMiddleTop = window.innerHeight * 0.3;
          const viewportMiddleBottom = window.innerHeight * 0.7;
          if (elRect.top < viewportMiddleTop || elRect.bottom > viewportMiddleBottom) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
      }
    }
  }, [currentTimeMs, transcript, activeSegmentIndex]);

  useEffect(() => {
    if (initialScrollToIndex === undefined || initialScrollToIndex === null) return;
    if (hasInitialScrolled.current) return;
    if (transcript.length === 0) return;
    hasInitialScrolled.current = true;
    setInitialHighlightIndex(initialScrollToIndex);
    setTimeout(() => {
      const el = document.getElementById(`${SEGMENT_ID_PREFIX}${initialScrollToIndex}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        onInitialScrollComplete?.();
      }
      setTimeout(() => setInitialHighlightIndex(null), 3000);
    }, 300);
  }, [initialScrollToIndex, transcript.length, onInitialScrollComplete]);

  // Build a map from speaker name/id to metadata (for image_url lookup)
  const speakerMetaMap = new Map<string, SpeakerMeta>();
  for (const meta of speakerMetas) {
    if (meta.display_name) speakerMetaMap.set(meta.display_name, meta);
    if (meta.id) speakerMetaMap.set(meta.id, meta);
  }

  // Assign avatar colours by order of appearance (not name-hash) so speakers
  // with similar names don't collide on the same colour.
  const speakerColorMap = new Map<string, string>();
  let __colorIdx = 0;
  for (const seg of transcript) {
    if (!speakerColorMap.has(seg.speaker)) {
      speakerColorMap.set(seg.speaker, AVATAR_COLORS[__colorIdx % AVATAR_COLORS.length]);
      __colorIdx++;
    }
  }

  // Existing speaker names for the speaker-edit autocomplete + match lookup.
  const uniqueSpeakerNames = Array.from(new Set(transcript.map((s) => s.speaker).filter(Boolean)));
  const canEditText = editable && !!onEditText;
  const canEditSpeaker = editable && !!onEditSpeaker;
  const canSplit = editable && !!onSplit;
  // Feature-flag: defaults OFF; flipped per spec L9 override after backend migration runs
  const speakerOverrideEnabled = (import.meta as any).env?.VITE_FEATURE_SEGMENT_SPEAKER_OVERRIDE === 'true';

  // Scissor cursor for word-click splitting in Edit mode. "4 4" is the hotspot.
  const SCISSOR_CURSOR = `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='%231f2430' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><circle cx='6' cy='6' r='3'/><circle cx='6' cy='18' r='3'/><line x1='20' y1='4' x2='8.12' y2='15.88'/><line x1='14.47' y1='14.48' x2='20' y2='20'/><line x1='8.12' y1='8.12' x2='12' y2='12'/></svg>") 4 4, pointer`;

  // --- Per-segment edit-mode state (single-segment-at-a-time) ---
  // editModeIdx is the segment index currently in edit mode (or null).
  // Snapshot captured on edit-mode enter; Cancel restores from snapshot.
  const [editModeIdx, setEditModeIdx] = useState<number | null>(null);
  const editSnapshotRef = useRef<Speaker | null>(null);

  const enterEditMode = useCallback((index: number) => {
    if (!editable) return;
    editSnapshotRef.current = { ...transcript[index] };
    setEditModeIdx(index);
    // Open the text editor immediately so the textarea is visible on the first click.
    setEditingTextIdx(index);
    setEditingTextValue(transcript[index]?.text ?? '');
  }, [editable, transcript]);

  const cancelEditMode = useCallback(() => {
    if (editModeIdx === null || !editSnapshotRef.current) {
      setEditModeIdx(null);
      setEditingTextIdx(null);
      setEditingTextValue('');
      return;
    }
    const snap = editSnapshotRef.current;
    const cur = transcript[editModeIdx];
    // Revert text if changed
    if (onEditText && cur.text !== snap.text) {
      onEditText(editModeIdx, snap.text);
    }
    // Revert speaker if changed
    if (onEditSpeaker && cur.speaker !== snap.speaker) {
      onEditSpeaker(cur.speaker, snap.speaker);
    }
    // Revert segment timestamps if changed
    if (onEditSegmentTs && (cur.start_ms !== snap.start_ms || cur.end_ms !== snap.end_ms)) {
      onEditSegmentTs(editModeIdx, snap.start_ms ?? 0, snap.end_ms ?? 0);
    }
    // Revert any per-word timestamp edits
    if (onEditWordTs && snap.words && cur.words) {
      for (let w = 0; w < snap.words.length && w < cur.words.length; w++) {
        const sw = snap.words[w];
        const cw = cur.words[w];
        if (sw.start_ms !== cw.start_ms || sw.end_ms !== cw.end_ms) {
          onEditWordTs(editModeIdx, w, sw.start_ms, sw.end_ms);
        }
      }
    }
    editSnapshotRef.current = null;
    setEditModeIdx(null);
    setEditingTextIdx(null);
    setEditingTextValue('');
  }, [editModeIdx, transcript, onEditText, onEditSpeaker, onEditSegmentTs, onEditWordTs]);

  // --- Text inline editing state ---
  const [editingTextIdx, setEditingTextIdx] = useState<number | null>(null);
  const [editingTextValue, setEditingTextValue] = useState('');
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (editingTextIdx !== null) textAreaRef.current?.focus();
  }, [editingTextIdx]);

  const startTextEdit = useCallback((index: number, currentText: string) => {
    if (!canEditText || editModeIdx !== index) return;
    setEditingTextIdx(index);
    setEditingTextValue(currentText);
  }, [canEditText, editModeIdx]);

  const commitTextEdit = useCallback(() => {
    if (editingTextIdx === null) return;
    const newText = editingTextValue.trim();
    if (newText && newText !== transcript[editingTextIdx]?.text) {
      onEditText?.(editingTextIdx, newText);
    }
    setEditingTextIdx(null);
  }, [editingTextIdx, editingTextValue, transcript, onEditText]);

  const cancelTextEdit = useCallback(() => {
    setEditingTextIdx(null);
  }, []);

  // Per-segment Save: commit the open textarea, persist all edits, then exit edit mode.
  // Defined after the text-edit state so it can read editingTextIdx/editingTextValue directly.
  const saveEditMode = useCallback(async () => {
    // Commit the open textarea's value to parent state SYNCHRONOUSLY before persisting.
    // Without flushSync, React 18 batches this update and onSaveAndExit reads stale
    // `editedSegments`, PATCHing old data to the backend — the "save twice" bug.
    if (editingTextIdx !== null) {
      flushSync(() => {
        const newText = editingTextValue.trim();
        if (newText && newText !== transcript[editingTextIdx]?.text) {
          onEditText?.(editingTextIdx, newText);
        }
        setEditingTextIdx(null);
      });
    }

    try {
      // Parent's editedSegments now has the latest text — onSaveAndExit persists correctly.
      if (onSaveAndExit) {
        await onSaveAndExit();
      }
    } finally {
      // Always exit edit mode, even if the persist throws.
      editSnapshotRef.current = null;
      setEditModeIdx(null);
      setEditingTextValue('');
    }
  }, [editingTextIdx, editingTextValue, transcript, onEditText, onSaveAndExit]);

  // --- Speaker inline editing state ---
  const [editingSpeakerIdx, setEditingSpeakerIdx] = useState<number | null>(null);
  const [editingSpeakerOldName, setEditingSpeakerOldName] = useState('');
  const [editingSpeakerValue, setEditingSpeakerValue] = useState('');
  const [speakerMatchError, setSpeakerMatchError] = useState('');

  const startSpeakerEdit = useCallback((index: number, currentName: string) => {
    if (!canEditSpeaker) return;
    setEditingSpeakerIdx(index);
    setEditingSpeakerOldName(currentName);
    setEditingSpeakerValue(currentName);
  }, [canEditSpeaker]);

  const commitSpeakerEdit = useCallback((mode: 'match' | 'all' | 'new') => {
    if (editingSpeakerIdx === null) return;
    const newName = editingSpeakerValue.trim();
    if (!newName) {
      setEditingSpeakerIdx(null);
      setSpeakerMatchError('');
      return;
    }
    if (newName === editingSpeakerOldName) {
      setEditingSpeakerIdx(null);
      setSpeakerMatchError('');
      return;
    }

    if (mode === 'all') {
      onEditSpeaker?.(editingSpeakerOldName, newName);
      setEditingSpeakerIdx(null);
      setSpeakerMatchError('');
      return;
    }

    if (mode === 'new') {
      onEditSpeakerSingle?.(editingSpeakerIdx, newName);
      setEditingSpeakerIdx(null);
      setSpeakerMatchError('');
      return;
    }

    // mode === 'match'
    const matched = uniqueSpeakerNames.find(
      (s) => s.toLowerCase().trim() === newName.toLowerCase()
    );
    if (matched) {
      onEditSpeakerSingle?.(editingSpeakerIdx, matched);
      setEditingSpeakerIdx(null);
      setSpeakerMatchError('');
    } else {
      setSpeakerMatchError(`No speaker matches "${newName}" — use New speaker to create.`);
      // Keep input open
    }
  }, [editingSpeakerIdx, editingSpeakerValue, editingSpeakerOldName, onEditSpeaker, onEditSpeakerSingle, uniqueSpeakerNames]);

  const cancelSpeakerEdit = useCallback(() => {
    setEditingSpeakerIdx(null);
    setSpeakerMatchError('');
  }, []);

  // --- Segment timestamp editing state ---
  const [editingTsIdx, setEditingTsIdx] = useState<number | null>(null);
  const [editingTsStartVal, setEditingTsStartVal] = useState('');
  const [editingTsEndVal, setEditingTsEndVal] = useState('');

  const commitSegTs = useCallback(() => {
    if (editingTsIdx === null || !onEditSegmentTs) return;
    const s = parseInt(editingTsStartVal, 10);
    const e = parseInt(editingTsEndVal, 10);
    if (!isNaN(s) && !isNaN(e)) onEditSegmentTs(editingTsIdx, s, e);
    setEditingTsIdx(null);
  }, [editingTsIdx, editingTsStartVal, editingTsEndVal, onEditSegmentTs]);

  // --- Split via gesture ---
  // Desktop: click a word in Edit view → split at that word (scissor cursor).
  // Mobile: double-tap word → direct split-after via onTouchEnd + lastTapRef (300ms inter-tap)
  const [splitErr, setSplitErr] = useState('');

  // Manual double-tap detection for iOS Safari touch path.
  // Inter-tap delta 300ms — matches iOS Safari's native double-tap window
  // and is short enough that two distinct intentional taps don't collide.
  const lastTapRef = useRef<{ time: number; segIdx: number; wordIdx: number } | null>(null);

  /** Returns true if splitting segIdx at splitAtMs would yield two children each >= MIN_CHILD_MS. */
  const canSplitAt = useCallback((segIdx: number, splitAtMs: number): boolean => {
    const seg = transcript[segIdx];
    if (!seg) return false;
    const segStart = seg.start_ms ?? 0;
    const segEnd = seg.end_ms ?? 0;
    if (splitAtMs <= segStart || splitAtMs >= segEnd) return false;
    if (splitAtMs - segStart < MIN_CHILD_MS) return false;
    if (segEnd - splitAtMs < MIN_CHILD_MS) return false;
    return true;
  }, [transcript]);

  const handleWordDoubleTap = useCallback((segIdx: number, wordIdx: number, splitAtMs: number) => {
    if (!canSplit) return;
    const now = Date.now();
    const prev = lastTapRef.current;
    if (prev && now - prev.time < 300 && prev.segIdx === segIdx && prev.wordIdx === wordIdx) {
      lastTapRef.current = null;
      if (!canSplitAt(segIdx, splitAtMs)) return; // silent MIN_CHILD_MS rejection
      // Bypasses the error-UI path (error state would flash the edit-mode bar on
      // backend failure). Touch errors are swallowed silently per brief.
      (async () => {
        try { await onSplit!(segIdx, splitAtMs, undefined); } catch { /* silent */ }
      })();
    } else {
      lastTapRef.current = { time: now, segIdx, wordIdx };
    }
  }, [canSplit, canSplitAt, onSplit]);

  const doSplit = useCallback(async (segIdx: number, splitAtMs: number) => {
    if (!onSplit) return;
    if (!canSplitAt(segIdx, splitAtMs)) {
      setSplitErr(`Split point invalid — both children must be ≥${MIN_CHILD_MS}ms`);
      return;
    }
    setSplitErr('');
    try {
      await onSplit(segIdx, splitAtMs, undefined);
    } catch (e) {
      setSplitErr(e instanceof Error ? e.message : 'Split failed');
    }
  }, [onSplit, canSplitAt]);

  // --- Timeline scrubber ---
  const [scrubberMs, setScrubberMs] = useState(0);
  const [scrubberSegmentIdx, setScrubberSegmentIdx] = useState<number | null>(null);
  const [scrubberTimeInput, setScrubberTimeInput] = useState('');
  const scrubberBarRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef(false);
  const [isDragging, setIsDragging] = useState(false);
  const rafRef = useRef<number | null>(null);

  const totalDurationMs = (() => {
    if (durationMs && durationMs > 0) return durationMs;
    const lastSeg = transcript[transcript.length - 1];
    if (!lastSeg) return 0;
    return lastSeg.end_ms || lastSeg.start_ms || 0;
  })();

  const showHours = totalDurationMs >= 3600000;

  useEffect(() => {
    setScrubberMs(0);
    setScrubberSegmentIdx(null);
    setScrubberTimeInput('');
  }, [transcript]);

  const findSegmentAtMs = useCallback((ms: number): number => {
    for (let i = transcript.length - 1; i >= 0; i--) {
      if ((transcript[i].start_ms || 0) <= ms) return i;
    }
    return 0;
  }, [transcript]);

  const scrubTo = useCallback((ms: number) => {
    const clampedMs = Math.max(0, Math.min(ms, totalDurationMs));
    setScrubberMs(clampedMs);
    const idx = findSegmentAtMs(clampedMs);
    setScrubberSegmentIdx(idx);
    const el = document.getElementById(`${SEGMENT_ID_PREFIX}${idx}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [totalDurationMs, findSegmentAtMs]);

  const scrubDrag = useCallback((ms: number) => {
    const clampedMs = Math.max(0, Math.min(ms, totalDurationMs));
    setScrubberMs(clampedMs);
    setScrubberSegmentIdx(findSegmentAtMs(clampedMs));
  }, [totalDurationMs, findSegmentAtMs]);

  const computeMsFromEvent = useCallback((clientX: number): number | null => {
    if (!scrubberBarRef.current || totalDurationMs === 0) return null;
    const rect = scrubberBarRef.current.getBoundingClientRect();
    const fraction = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    return fraction * totalDurationMs;
  }, [totalDurationMs]);

  const handleScrubberClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (isDraggingRef.current) return;
    const ms = computeMsFromEvent(e.clientX);
    if (ms !== null) scrubTo(ms);
  }, [computeMsFromEvent, scrubTo]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDraggingRef.current) return;
      e.preventDefault();
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        const ms = computeMsFromEvent(e.clientX);
        if (ms !== null) scrubDrag(ms);
      });
    };
    const handleMouseUp = (e: MouseEvent) => {
      if (!isDraggingRef.current) return;
      isDraggingRef.current = false;
      setIsDragging(false);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      const ms = computeMsFromEvent(e.clientX);
      if (ms !== null) scrubTo(ms);
    };
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [computeMsFromEvent, scrubTo, scrubDrag]);

  const handleTimeInputSubmit = useCallback(() => {
    const ms = parseTimeToMs(scrubberTimeInput);
    if (ms !== null) scrubTo(ms);
  }, [scrubberTimeInput, scrubTo]);

  const scrubberMarkers = (() => {
    if (totalDurationMs === 0) return [];
    const intervalMs = totalDurationMs <= 300000 ? 60000 : totalDurationMs <= 1800000 ? 300000 : 600000;
    const markers: { ms: number; label: string }[] = [];
    for (let ms = 0; ms <= totalDurationMs; ms += intervalMs) {
      markers.push({ ms, label: msToDuration(ms, showHours) });
    }
    return markers;
  })();

  const effectiveHighlight = initialHighlightIndex ?? scrubberSegmentIdx ?? (activeSegmentIndex >= 0 ? activeSegmentIndex : highlightIndex);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className={`w-full max-w-none sm:max-w-4xl lg:max-w-6xl mx-auto px-1 sm:px-4 ${hasAudioBarAbove ? 'pt-0 pb-2 sm:pb-8' : 'py-2 sm:py-8'}`}
    >
      {/* Timeline scrubber — disabled for now, needs more polish */}
      {false && transcript.length > 0 && totalDurationMs > 0 && (
        <div className="mb-2 sticky top-[68px] z-10 bg-white/95 backdrop-blur-sm rounded-lg border border-gray-200 px-4 py-3">
          <div className="flex items-center gap-3">
            <input
              type="text"
              value={scrubberTimeInput}
              onChange={(e) => setScrubberTimeInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleTimeInputSubmit(); }}
              placeholder={showHours ? '00:00:00' : '00:00'}
              className="w-[72px] rounded border border-gray-300 px-2 py-1 text-xs tabular-nums text-gray-700 placeholder-gray-300 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400 font-mono"
            />
            <div className="relative flex-1">
              <div
                ref={scrubberBarRef}
                className="relative h-2 cursor-pointer rounded-full bg-gray-200"
                onClick={handleScrubberClick}
                onMouseDown={() => { isDraggingRef.current = true; setIsDragging(true); }}
              >
                <div
                  className="absolute top-0 left-0 h-full rounded-full bg-blue-500 pointer-events-none"
                  style={{
                    width: `${totalDurationMs > 0 ? (scrubberMs / totalDurationMs) * 100 : 0}%`,
                    transition: isDragging ? 'none' : 'width 0.15s ease-out',
                  }}
                />
                <div
                  className="absolute top-1/2 -translate-y-1/2 h-4 w-4 rounded-full bg-blue-600 border-2 border-white shadow-sm pointer-events-none"
                  style={{
                    left: `calc(${totalDurationMs > 0 ? (scrubberMs / totalDurationMs) * 100 : 0}% - 8px)`,
                    transition: isDragging ? 'none' : 'left 0.15s ease-out',
                  }}
                />
              </div>
              <div className="relative mt-1 h-3">
                {scrubberMarkers.map((m) => (
                  <span
                    key={m.ms}
                    className="absolute text-[10px] text-gray-400 -translate-x-1/2 tabular-nums font-mono"
                    style={{ left: `${(m.ms / totalDurationMs) * 100}%` }}
                  >
                    {m.label}
                  </span>
                ))}
              </div>
            </div>
            <span className="shrink-0 text-xs text-gray-400 tabular-nums font-mono">
              {msToDuration(totalDurationMs, showHours)}
            </span>
          </div>
        </div>
      )}

      <div ref={scrollContainerRef} className="space-y-1.5">
          {transcript.map((item, index) => {
            const isHighlighted = effectiveHighlight === index;
            const isInEditMode = editModeIdx === index;

            return (
              <motion.div
                key={index}
                id={`${SEGMENT_ID_PREFIX}${index}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.04 }}
                className={`group relative pr-[50px] py-2 ${
                  isInEditMode
                    ? 'rounded-lg bg-sky-50/30 ring-1 ring-inset ring-[#2563eb]/20'
                    : isHighlighted
                    ? 'rounded-lg bg-sky-100/60'
                    : ''
                }`}
              >
                <div className="flex flex-col sm:grid sm:grid-cols-[56px_1fr] sm:gap-x-3">

                  {/* Gutter: avatar + timestamp */}
                  <div className="flex sm:flex-col items-center sm:items-center gap-2 sm:gap-[7px] mb-2 sm:mb-0 sm:pt-0.5">
                    <SpeakerAvatar
                      name={item.speaker}
                      imageUrl={speakerMetaMap.get(item.speaker)?.image_url}
                      size="lg"
                      className={speakerColorMap.get(item.speaker)}
                    />
                    {(item.timestamp || item.start_ms != null) && (
                      editingTsIdx === index ? (
                        <div className="flex flex-col gap-1">
                          <input
                            autoFocus
                            value={editingTsStartVal}
                            onChange={(e) => setEditingTsStartVal(e.target.value)}
                            className="w-20 rounded border border-[#2563eb]/40 px-1 py-0.5 text-[10px] tabular-nums font-mono focus:outline-none focus:ring-1 focus:ring-[#2563eb]"
                            placeholder="start ms"
                            onKeyDown={(e) => { if (e.key === 'Enter') commitSegTs(); if (e.key === 'Escape') setEditingTsIdx(null); }}
                            onBlur={commitSegTs}
                          />
                          <input
                            value={editingTsEndVal}
                            onChange={(e) => setEditingTsEndVal(e.target.value)}
                            className="w-20 rounded border border-[#2563eb]/40 px-1 py-0.5 text-[10px] tabular-nums font-mono focus:outline-none focus:ring-1 focus:ring-[#2563eb]"
                            placeholder="end ms"
                            onKeyDown={(e) => { if (e.key === 'Enter') commitSegTs(); if (e.key === 'Escape') setEditingTsIdx(null); }}
                            onBlur={commitSegTs}
                          />
                        </div>
                      ) : (
                        <div className="flex items-center gap-0.5">
                          {onSeekAudio ? (
                            <button
                              onClick={() => onSeekAudio(item.start_ms || 0)}
                              className="shrink-0 text-[10px] tabular-nums font-mono text-[#2563eb] hover:text-[#1d4ed8] hover:underline cursor-pointer"
                              title="Click to jump to this timestamp"
                            >
                              {formatTimestamp(item.timestamp || msToDuration(item.start_ms || 0, true), showHours)}
                            </button>
                          ) : (
                            <span className="shrink-0 text-[10px] tabular-nums font-mono text-[#8a90a0]">
                              {formatTimestamp(item.timestamp || msToDuration(item.start_ms || 0, true), showHours)}
                            </span>
                          )}
                          {isInEditMode && onEditSegmentTs && (
                            <button
                              onClick={() => {
                                setEditingTsIdx(index);
                                setEditingTsStartVal(String(item.start_ms ?? 0));
                                setEditingTsEndVal(String(item.end_ms ?? 0));
                              }}
                              className="ml-0.5 shrink-0 text-[9px] text-[#8a90a0] hover:text-[#2563eb]"
                              title="Edit segment timestamps"
                            >
                              ✎
                            </button>
                          )}
                        </div>
                      )
                    )}
                  </div>

                  {/* Body column */}
                  <div className="min-w-0">
                    {/* Pencil affordance — 30px square, hover-revealed on desktop.
                        Positioned against the card (motion.div) so it sits flush in the right gutter. */}
                    {editable && !isInEditMode && (
                      <button
                        onClick={() => enterEditMode(index)}
                        className="absolute right-3 top-3 inline-flex h-[30px] w-[30px] items-center justify-center rounded-md border border-[#e7e9ee] bg-white/90 text-[#8a90a0] shadow-sm transition-opacity hover:border-[#2563eb] hover:text-[#2563eb] focus:outline-none sm:opacity-0 sm:group-hover:opacity-100 sm:focus:opacity-100"
                        title="Edit this segment"
                        aria-label={`Edit segment ${index + 1}`}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
                          <path d="m5.433 13.917 1.262-3.155A4 4 0 0 1 7.58 9.42l6.92-6.918a2.121 2.121 0 0 1 3 3l-6.92 6.918c-.383.383-.84.685-1.343.886l-3.154 1.262a.5.5 0 0 1-.65-.65Z" />
                          <path d="M3.5 5.75c0-.69.56-1.25 1.25-1.25H10A.75.75 0 0 0 10 3H4.75A2.75 2.75 0 0 0 2 5.75v9.5A2.75 2.75 0 0 0 4.75 18h9.5A2.75 2.75 0 0 0 17 15.25V10a.75.75 0 0 0-1.5 0v5.25c0 .69-.56 1.25-1.25 1.25h-9.5c-.69 0-1.25-.56-1.25-1.25v-9.5Z" />
                        </svg>
                      </button>
                    )}

                    {/* Speaker name — UPPERCASE 12px label */}
                    {canEditSpeaker && editingSpeakerIdx === index ? (
                      <div className="mb-1">
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            list={`speakers-${index}`}
                            autoFocus
                            value={editingSpeakerValue}
                            onChange={(e) => { setEditingSpeakerValue(e.target.value); setSpeakerMatchError(''); }}
                            onBlur={() => commitSpeakerEdit('match')}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') commitSpeakerEdit('match');
                              if (e.key === 'Escape') cancelSpeakerEdit();
                            }}
                            className="rounded border border-[#2563eb]/40 px-1 py-0.5 text-sm font-semibold text-[#1f2430] outline-none focus:ring-1 focus:ring-[#2563eb]"
                          />
                          <datalist id={`speakers-${index}`}>
                            {uniqueSpeakerNames.map((s) => (
                              <option key={s} value={s} />
                            ))}
                          </datalist>
                          <button
                            type="button"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => commitSpeakerEdit('new')}
                            className="rounded border border-[#e7e9ee] bg-white px-2 py-0.5 text-[10px] font-medium text-[#4a5160] hover:bg-[#f0f4ff] hover:text-[#2563eb]"
                          >
                            New speaker
                          </button>
                          <button
                            type="button"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => commitSpeakerEdit('all')}
                            className="rounded border border-[#e7e9ee] bg-white px-2 py-0.5 text-[10px] font-medium text-[#4a5160] hover:bg-[#f0f4ff] hover:text-[#2563eb]"
                          >
                            Apply to all
                          </button>
                        </div>
                        {speakerMatchError && (
                          <p className="mt-1 text-[10px] text-amber-700">{speakerMatchError}</p>
                        )}
                      </div>
                    ) : (
                      <span
                        className={`mb-1 block text-sm font-semibold text-[#1f2430]${canEditSpeaker ? ' cursor-pointer hover:text-[#2563eb] hover:underline' : ''}`}
                        onClick={canEditSpeaker ? () => startSpeakerEdit(index, item.speaker) : undefined}
                        title={canEditSpeaker ? 'Click to edit speaker. Enter = match existing. New speaker = create new. Apply to all = rename everywhere.' : undefined}
                      >
                        {item.speaker}
                      </span>
                    )}

                    {/* Pending-review badge (LM2: one gate here, not per-view) */}
                    {speakerOverrideEnabled && item.user_override && (
                      <span className="mb-1 inline-flex items-center rounded-full bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-700">
                        pending review
                      </span>
                    )}

                    {/* Segment text — full width */}
                    <div className="w-full">
                      {editingTextIdx === index ? (
                        <textarea
                          ref={textAreaRef}
                          value={editingTextValue}
                          onChange={(e) => setEditingTextValue(e.target.value)}
                          onBlur={commitTextEdit}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              commitTextEdit();
                            }
                            if (e.key === 'Escape') cancelTextEdit();
                          }}
                          className="mt-0.5 w-full resize-none rounded border border-[#e7e9ee] px-2 py-1 text-base text-[#1f2430] outline-none focus:border-[#2563eb] focus:ring-1 focus:ring-[#2563eb]"
                          style={{ lineHeight: '1.65' }}
                          rows={Math.max(2, Math.ceil(editingTextValue.length / 80))}
                        />
                      ) : (
                        <div
                          className={`relative mt-0.5 rounded px-1 -mx-1${(canEditText && isInEditMode) ? ' cursor-text hover:bg-white/60' : ''}`}
                          onClick={(canEditText && isInEditMode) ? () => startTextEdit(index, item.text) : undefined}
                          title={(canEditText && isInEditMode) ? 'Click to edit text' : undefined}
                        >
                          <p className="text-base leading-[1.65] text-[#1f2430] text-justify">
                            {item.words && item.words.length > 0 ? (
                              item.words.map((word, wIdx) => {
                                const wordText = word.text || (word as any).word || '';
                                const wordStart = word.start_ms ?? (word as any).start ?? 0;
                                const wordEnd = word.end_ms ?? (word as any).end ?? 0;
                                const isCurrentWord = currentTimeMs > 0 && currentTimeMs >= wordStart && currentTimeMs < wordEnd;
                                const isPastWord = currentTimeMs > 0 && currentTimeMs >= wordEnd;
                                const canSeek = !!onSeekAudio;

                                return (
                                  <Fragment key={wIdx}>
                                    <span
                                      data-word-touchable
                                      onClick={
                                        isInEditMode
                                          ? undefined
                                          : (e) => {
                                              e.stopPropagation();
                                              if (editable && wIdx > 0) {
                                                doSplit(index, wordStart); // cut BEFORE this word → it starts the new segment
                                              } else if (canSeek && onSeekAudio) {
                                                onSeekAudio(wordStart);
                                              }
                                            }
                                      }
                                      onTouchEnd={canSplit ? (e) => { e.stopPropagation(); handleWordDoubleTap(index, wIdx, wordEnd); } : undefined}
                                      style={{
                                        touchAction: 'manipulation',
                                        cursor:
                                          editable && !isInEditMode && wIdx > 0
                                            ? SCISSOR_CURSOR
                                            : isInEditMode
                                            ? 'text'
                                            : canSeek
                                            ? 'pointer'
                                            : 'default',
                                      }}
                                      className={`${
                                        isCurrentWord
                                          ? 'rounded px-0.5 bg-sky-200 text-[#1f2430] font-semibold ring-1 ring-sky-400 transition-colors duration-75 cursor-pointer'
                                          : isPastWord
                                          ? 'text-[#a7adba] transition-colors duration-150' + (canSeek ? ' cursor-pointer hover:text-[#4a5160] rounded px-0.5' : '')
                                          : 'text-[#1f2430]' + (canSeek ? ' cursor-pointer hover:bg-sky-50 hover:text-[#2563eb] rounded px-0.5' : '')
                                      }${editable && !isInEditMode && wIdx > 0 ? ' cut-target' : ''}`}
                                      title={
                                        isInEditMode
                                          ? undefined
                                          : editable && wIdx > 0
                                          ? 'Cut the script here'
                                          : canSeek
                                          ? `Jump to ${wordText}`
                                          : undefined
                                      }
                                    >
                                      {wordText}
                                    </span>
                                    {' '}
                                  </Fragment>
                                );
                              })
                            ) : (
                              item.text
                            )}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Merge buttons — hover-revealed at bottom-right, mirroring pencil pattern */}
                    {editable && (onMergeWithPrev || onMergeWithNext) && !isInEditMode && (
                      <div className="mt-1.5 flex justify-end gap-1 sm:absolute sm:bottom-3 sm:right-3 sm:mt-0 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                        {index > 0 && onMergeWithPrev && (
                          <button
                            type="button"
                            onClick={() => onMergeWithPrev(index)}
                            className="inline-flex h-[22px] w-[22px] items-center justify-center rounded border border-[#e7e9ee] bg-white/90 text-[#4a5160] shadow-sm hover:bg-[#f0f4ff] hover:text-[#2563eb] hover:border-[#2563eb]"
                            title="Merge with previous segment"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="h-3.5 w-3.5">
                              <path d="M5 9h14"/><path d="M12 9V3"/><path d="m8 6 4-3 4 3"/><path d="M7 14h10M9 19h6"/>
                            </svg>
                          </button>
                        )}
                        {index < transcript.length - 1 && onMergeWithNext && (
                          <button
                            type="button"
                            onClick={() => onMergeWithNext(index)}
                            className="inline-flex h-[22px] w-[22px] items-center justify-center rounded border border-[#e7e9ee] bg-white/90 text-[#4a5160] shadow-sm hover:bg-[#f0f4ff] hover:text-[#2563eb] hover:border-[#2563eb]"
                            title="Merge with next segment"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="h-3.5 w-3.5">
                              <path d="M5 15h14"/><path d="M12 15v6"/><path d="m8 18 4 3 4-3"/><path d="M7 10h10M9 5h6"/>
                            </svg>
                          </button>
                        )}
                      </div>
                    )}

                    {/* Inline Save/Cancel + split error — only in edit mode */}
                    {isInEditMode && (
                      <>
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                          <button
                            type="button"
                            onClick={saveEditMode}
                            className="rounded bg-[#2563eb] px-3 py-1 text-xs font-medium text-white hover:bg-[#1d4ed8]"
                          >
                            Save
                          </button>
                          <button
                            type="button"
                            onClick={cancelEditMode}
                            className="rounded border border-[#e7e9ee] bg-white px-3 py-1 text-xs text-[#4a5160] hover:bg-gray-50"
                          >
                            Cancel
                          </button>
                          {/* Merge buttons in edit mode — shown inline (no hover-only) */}
                          {(onMergeWithPrev || onMergeWithNext) && (
                            <>
                              {index > 0 && onMergeWithPrev && (
                                <button
                                  type="button"
                                  onClick={() => onMergeWithPrev(index)}
                                  className="inline-flex h-[28px] w-[28px] items-center justify-center rounded border border-[#e7e9ee] bg-white text-[#4a5160] hover:bg-[#f0f4ff] hover:text-[#2563eb]"
                                  title="Merge with previous segment"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="h-4 w-4">
                                    <path d="M5 9h14"/><path d="M12 9V3"/><path d="m8 6 4-3 4 3"/><path d="M7 14h10M9 19h6"/>
                                  </svg>
                                </button>
                              )}
                              {index < transcript.length - 1 && onMergeWithNext && (
                                <button
                                  type="button"
                                  onClick={() => onMergeWithNext(index)}
                                  className="inline-flex h-[28px] w-[28px] items-center justify-center rounded border border-[#e7e9ee] bg-white text-[#4a5160] hover:bg-[#f0f4ff] hover:text-[#2563eb]"
                                  title="Merge with next segment"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="h-4 w-4">
                                    <path d="M5 15h14"/><path d="M12 15v6"/><path d="m8 18 4 3 4-3"/><path d="M7 10h10M9 5h6"/>
                                  </svg>
                                </button>
                              )}
                            </>
                          )}
                          {splitErr && <span className="text-xs text-red-600">{splitErr}</span>}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })}
      </div>
    </motion.div>
  );
}
